import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrderDataPrices, OrdersDataType } from "./orders.reducer";
import store from "../store";
import { Security } from "../proto/generated/security";
import { getSecurityAsync, getSecurityKey } from "../util/security";
import { setPredefinedStrategy } from "./strategyBuilder.reducer";
import { getLastWeekday, getPreviousValidTradingDay } from "../util/tradingCalendar";
import { TradingCalendarData } from "../proto/generated/trading-calendar";

export interface SimulatorStateType {
    timestamp: number
    orders: OrdersDataType[]
    SquareOffOrders: OrdersDataType[]
    ordersPrices: OrderDataPrices[]
    orderSecurityKey: string | undefined
    autoPlay: boolean
    initialTimestampSet: boolean
}

const initialState: SimulatorStateType = {
    timestamp: getLastWeekday(),
    orders: [],
    SquareOffOrders: [],
    ordersPrices: [],
    orderSecurityKey: undefined,
    autoPlay: false,
    initialTimestampSet: false
};

const simulatorSlice = createSlice({
    name: "simulator",
    initialState,
    reducers: {
        setSimulatorTimestamp(state, action: PayloadAction<number>) {
            // If timestamp is 0 or undefined, it will be handled by initializeSimulatorTimestamp
            if (action.payload && action.payload > 0) {
                state.timestamp = action.payload;
                state.initialTimestampSet = true;
            }
        },
        initializeSimulatorTimestamp(state, action: PayloadAction<TradingCalendarData | undefined>) {
            // Only set if we haven't manually set a timestamp yet
            if (!state.initialTimestampSet && action.payload) {
                const now = new Date()
                const previousTradingDay = getPreviousValidTradingDay(now, action.payload)
                if (previousTradingDay) {
                    // Set to 9:16 AM on the previous trading day
                    previousTradingDay.setHours(9, 16, 0, 0)
                    state.timestamp = previousTradingDay.getTime()
                }
                state.initialTimestampSet = true
            }
        },
        setSimulatorOrderData(state, action: PayloadAction<{ orders: OrdersDataType[], security?: string, reset?: boolean, orderPrices?: OrderDataPrices[] }>) {
            state.orders = action.payload.orders
            state.orderSecurityKey = action.payload.security || state.orderSecurityKey
            if (action.payload.reset) {
                state.orders = []
                state.ordersPrices = []
                state.SquareOffOrders = []
                state.orderSecurityKey = ''
            }
            if (action.payload.orderPrices) {
                state.ordersPrices = action.payload.orderPrices
            }
        },
        setAutoPlay(state, action: PayloadAction<boolean>) {
            state.autoPlay = action.payload
        }
    },
});

const { actions, reducer: simulatorReducer } = simulatorSlice;

export const { setSimulatorTimestamp, setSimulatorOrderData, setAutoPlay, initializeSimulatorTimestamp } = actions;

export default simulatorReducer;

export const setSimulatorOrderDataFunction = async ({ orders, token = '', resetPredefinedStrategy = true, resetOrderState = false, orderPrices = undefined, strategyName }: { orders: OrdersDataType[], token?: string, resetPredefinedStrategy?: boolean, resetOrderState?: boolean, orderPrices?: OrderDataPrices[], strategyName?: string }) => {
    if(token === 'EXCHANGE_UNKNOWN:') return
    if(strategyName) {
        store.dispatch(setPredefinedStrategy(strategyName))
    } else if (resetPredefinedStrategy) {
        store.dispatch(setPredefinedStrategy(undefined))
    }
    const primarySecurity = store.getState()?.homeStore?.primarySecurity
    let secKey = primarySecurity?.key
    if (primarySecurity?.expressionMode) {
        const sec = await getSecurityAsync(primarySecurity?.securities?.[0] ?? "")
        secKey = sec ? getSecurityKey(sec.exchange, sec.underlyingSecurityCode ?? '') : secKey
    }
    const newOrders: OrdersDataType[] = []
    orders.forEach(order => {
        let newOrder = { ...order }
        if (newOrder.contractType === 'OPTION' && newOrder.data.callOption && newOrder.data.putOption) {
            const security = newOrder.callput === 'call' ? newOrder.data.callOption.security : newOrder.data.putOption.security
            newOrder = {
                ...newOrder,
                data: {
                    ...newOrder.data,
                    callOption: {
                        ...newOrder.data.callOption
                    },
                    putOption: {
                        ...newOrder.data.putOption
                    }
                },
                security: security,
                expiry: security.expiry?.expiry ?? 0
            }
        } else if (newOrder.contractType === 'FUTURE' && newOrder.data.security) {
            newOrder = {
                ...newOrder,
                data: {
                    ...newOrder.data,
                    security: newOrder.data.security
                },
                security: newOrder.data.security,
                expiry: newOrder.data.security.expiry?.expiry ?? 0
            }
        }
        newOrders.push(newOrder)
    })
    store.dispatch(setSimulatorOrderData({ orders: newOrders, security: token ? token : secKey, reset: resetOrderState, orderPrices: orderPrices }))
}